import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { QUIZ, quizKey } from "../../data/games";
import type { QuizStat } from "../../types";
import { fetchQuizStats } from "../../utils/api";
import { AppHeader, Banner, Icon } from "../common/ui";

interface Props {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}

export default function AdminQuizStats({ token, onExit, theme, onThemeChange }: Props) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const pick = (b: { ru: string; en: string }) => (isRu ? b.ru : b.en || b.ru);

  const [stats, setStats] = useState<QuizStat[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchQuizStats(token).then(setStats).catch((e) => setError(e instanceof Error ? e.message : t("common.error")));
  }, [token, t]);

  const byKey = useMemo(() => {
    const m: Record<string, QuizStat> = {};
    for (const s of stats ?? []) m[s.key] = s;
    return m;
  }, [stats]);

  const overall = useMemo(() => {
    let total = 0, correct = 0;
    for (const s of stats ?? []) { total += s.total; correct += s.correct; }
    return { total, correct, pct: total ? Math.round((correct / total) * 100) : 0 };
  }, [stats]);

  return (
    <div className="beeapp__scroll">
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("quizStats.title")} />
      <div className="wrap content-cap" style={{ paddingTop: 6, maxWidth: 720 }}>
        {error && <div style={{ marginBottom: 12 }}><Banner kind="error">{error}</Banner></div>}

        {/* Overall summary */}
        <div className="glass glass-lg" style={{ padding: 18, marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{
            display: "grid", placeItems: "center", width: 48, height: 48, borderRadius: 14,
            background: "linear-gradient(160deg, var(--accent-fill), var(--accent-fill-2))", color: "var(--accent-ink)", flex: "none",
          }}>
            <Icon name="trophy" size={24} />
          </span>
          <div>
            <p className="eyebrow" style={{ marginBottom: 2 }}>{t("quizStats.overall")}</p>
            <p className="t-h" style={{ margin: 0 }}>
              {overall.pct}% {t("quizStats.correct")} · {overall.total} {t("quizStats.answers")}
            </p>
          </div>
        </div>

        {stats === null ? (
          <div style={{ display: "grid", placeItems: "center", padding: 40 }}>
            <div className="spinner spinner-amber" style={{ width: 28, height: 28, borderWidth: 3 }} />
          </div>
        ) : (
          <div className="stack" style={{ "--gap": "12px" } as React.CSSProperties}>
            {QUIZ.map((q, qi) => {
              const s = byKey[quizKey(q)];
              const total = s?.total ?? 0;
              const correct = s?.correct ?? 0;
              const pct = total ? Math.round((correct / total) * 100) : null;
              return (
                <div key={qi} className="glass glass-lg" style={{ padding: 16 }}>
                  <div className="row between" style={{ gap: 8, marginBottom: 10, alignItems: "flex-start" }}>
                    <span className="t-sm" style={{ fontWeight: 700, lineHeight: 1.35 }}>{qi + 1}. {pick(q.q)}</span>
                    {total > 0
                      ? <span className={`badge ${pct! >= 70 ? "badge-success" : pct! >= 40 ? "badge-amber" : "badge-info"}`} style={{ flex: "none" }}>{pct}%</span>
                      : <span className="badge" style={{ flex: "none" }}>{t("quizStats.noAnswers")}</span>}
                  </div>
                  <div className="stack" style={{ "--gap": "6px" } as React.CSSProperties}>
                    {q.options.map((opt, oi) => {
                      const cnt = s?.options?.[String(oi)] ?? 0;
                      const optPct = total ? Math.round((cnt / total) * 100) : 0;
                      const isCorrect = oi === q.correct;
                      return (
                        <div key={oi}>
                          <div className="row between" style={{ gap: 8, marginBottom: 2 }}>
                            <span className="t-sm" style={{ fontWeight: isCorrect ? 700 : 500, color: isCorrect ? "var(--success)" : "var(--text-2)" }}>
                              {isCorrect && <Icon name="check" size={12} stroke={2.6} />} {pick(opt)}
                            </span>
                            <span className="t-xs muted-2" style={{ flex: "none" }}>{cnt} · {optPct}%</span>
                          </div>
                          <div style={{ height: 6, borderRadius: 99, background: "var(--glass-1)", overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${optPct}%`, background: isCorrect ? "var(--success)" : "var(--accent-fill)", borderRadius: 99 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="t-xs muted-2" style={{ marginTop: 8 }}>{t("quizStats.totalAnswers")}: {total}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
